import { inject, Injectable, Signal, signal } from '@angular/core';
import { IHiddenWidgets } from '../interfaces/hidden-widgets.interface';
import { HttpClient } from '@angular/common/http';
import { Constants } from '../enums/constants.enum';
import { firstValueFrom } from 'rxjs';
import { APP_CONFIG } from '../../app.tokens';
import { isNullOrUndefined } from 'is-what';
import { IConfigData } from '../interfaces/config-data.interface';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private http = inject(HttpClient);
  config = inject(APP_CONFIG);

  private publicKeySignal = signal<string>('');
  publicKey: Signal<string> = this.publicKeySignal.asReadonly();

  private configDataSignal = signal<IConfigData>(
    null as unknown as IConfigData,
  );
  configData: Signal<IConfigData> = this.configDataSignal.asReadonly();

  isLoadData = signal(false);

  /**
   * Retrieves the list of user-hidden widgets from the settings API.
   *
   * Performs an HTTP GET request to the hidden widgets endpoint and resolves
   * with the deserialized response body. If the server responds successfully
   * but the body is null or undefined, a fallback object containing an empty
   * hiddenWidgets array is returned to simplify downstream handling.
   *
   * Errors (e.g. network issues, non-2xx status codes) will reject the returned
   * Promise with the underlying HttpErrorResponse.
   *
   * @returns A promise that resolves to an IHiddenWidgets object representing
   *          the collection of currently hidden widgets (empty if none).
   *
   * @example
   * const { hiddenWidgets } = await settingsService.getHiddenWidgets();
   * if (!hiddenWidgets.includes('weather')) {
   *   // Show weather widget
   * }
   */
  async getHiddenWidgets(): Promise<IHiddenWidgets> {
    const widgetData$ = this.http.get<IHiddenWidgets>(
      `${this.config.apiUrl}/${Constants.SETTINGS}/${Constants.HIDDEN_WIDGETS}`,
      {
        observe: 'response',
      },
    );

    const widgetData = await firstValueFrom(widgetData$);
    if (isNullOrUndefined(widgetData.body)) {
      return { hiddenWidgets: [] };
    }
    return widgetData.body as IHiddenWidgets;
  }

  /**
   * Updates the in‑memory configuration data signal with the provided object.
   *
   * Use this to replace the current configuration values (e.g. feature flags,
   * environment settings, or UI tuning parameters) that other parts of the
   * application consume via reactive signals.
   *
   * The update is atomic: any subscribers to the underlying signal will be
   * notified exactly once with the fully new configuration snapshot.
   *
   * Validation or transformation should be performed by the caller prior
   * to invoking this method; this setter assumes the payload is already
   * sanitized and complete.
   *
   * @param data The new configuration data object to apply.
   *
   * @example
   * // Load configuration (e.g. from an HTTP request) and apply it:
   * settingsService.setConfigData(response.config);
   *
   * @remarks
   * - Prefer calling this during application bootstrap or after a successful
   *   remote fetch/refresh operation.
   * - If partial updates are needed, retrieve the current value, merge, and
   *   pass the merged result back into this method.
   *
   * @throws No exceptions are thrown by this method itself; downstream
   * reactive consumers should handle any assumptions about required fields.
   */
  setConfigData(data: IConfigData) {
    this.configDataSignal.set(data);
  }

  /**
   * Updates the reactive public key value used by the application for cryptographic operations
   * (e.g., encrypting payloads before transmission).
   *
   * This method pushes a new key string into the underlying signal, allowing any dependent
   * consumers (components, services, or effects) to react to the change immediately.
   *
   * Usage considerations:
   * - Prefer passing a validated, sanitized key (e.g., PEM or Base64-encoded) to avoid downstream errors.
   * - If rotation is frequent, debounce external triggers before calling to reduce unnecessary reactive churn.
   * - Downstream logic should handle empty or malformed keys gracefully.
   *
   * @param key The new public key string to set. Expected to be a non-empty, correctly formatted representation.
   * @returns void
   */
  setPublicKey(key: string) {
    this.publicKeySignal.set(key);
  }

  async getAPIPublicKey(): Promise<string> {
    const data$ = this.http.get<{ publicKey: string }>(
      `${this.config.apiUrl}/${Constants.SETTINGS}/${Constants.SIGNATURE_PUBLIC_KEY}`,
      {
        observe: 'response',
      },
    );

    const data = await firstValueFrom(data$);
    return data.body?.publicKey as string;
  }
}
